import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Plus, Pencil, Trash2, RefreshCw, Users, Shield, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get stored credentials from sessionStorage
const getAuthHeader = () => {
  const creds = sessionStorage.getItem('lyca_auth');
  if (creds) {
    return { Authorization: `Basic ${creds}` };
  }
  return {};
};

// Get current username from stored credentials
const getCurrentUsername = () => {
  const creds = sessionStorage.getItem('lyca_auth');
  if (creds) {
    try {
      const decoded = atob(creds);
      return decoded.split(':')[0];
    } catch {
      return null;
    }
  }
  return null;
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    name: "",
    password: "",
  });

  const currentUsername = getCurrentUsername();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      const response = await axios.get(`${API}/admin-users`, { headers });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching admin users:", error);
      toast.error("Failed to load admin users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        username: user.username,
        name: user.name,
        password: "", // Don't show existing password
      });
    } else {
      setSelectedUser(null);
      setFormData({
        username: "",
        name: "",
        password: "",
      });
    }
    setShowPassword(false);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeader();
      
      if (selectedUser) {
        // Update - only send fields that have values
        const updateData = { name: formData.name };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API}/admin-users/${selectedUser.id}`, updateData, { headers });
        toast.success("Admin user updated successfully");
      } else {
        // Create - require password
        if (!formData.password) {
          toast.error("Password is required for new users");
          return;
        }
        await axios.post(`${API}/admin-users`, formData, { headers });
        toast.success("Admin user created successfully");
      }
      setIsDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error("Error saving admin user:", error);
      toast.error(error.response?.data?.detail || "Failed to save admin user");
    }
  };

  const handleDelete = async () => {
    try {
      const headers = getAuthHeader();
      await axios.delete(`${API}/admin-users/${selectedUser.id}`, { headers });
      toast.success("Admin user deleted successfully");
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error deleting admin user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete admin user");
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 text-[#00D26A] animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-white" data-testid="admin-users-title">
            Admin Users
          </h1>
          <p className="text-slate-400 mt-1">Manage system administrators</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="btn-primary flex items-center gap-2"
          data-testid="add-admin-user-btn"
        >
          <Plus className="w-4 h-4" />
          Add Admin User
        </Button>
      </div>

      {/* Users Grid */}
      {users.length === 0 ? (
        <div className="card">
          <div className="empty-state py-12">
            <Users className="w-12 h-12 text-slate-600 mb-4" />
            <p className="text-lg">No admin users</p>
            <p className="text-sm">Create your first admin user to get started.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-users-grid">
          {users.map((user) => (
            <div
              key={user.id}
              className={`card p-6 hover:border-[#0066FF]/30 transition-colors ${user.username === currentUsername ? 'border-[#00D26A]/30' : ''}`}
              data-testid={`admin-user-card-${user.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#0066FF]/10 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#0066FF]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{user.name}</h3>
                    <p className="text-sm text-slate-400 font-mono">@{user.username}</p>
                  </div>
                </div>
                {user.username !== currentUsername && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenDialog(user)}
                      className="p-2 hover:bg-slate-800 rounded transition-colors"
                      data-testid={`edit-admin-user-${user.id}`}
                    >
                      <Pencil className="w-4 h-4 text-[#0066FF]" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setIsDeleteDialogOpen(true);
                      }}
                      className="p-2 hover:bg-slate-800 rounded transition-colors"
                      data-testid={`delete-admin-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-500 space-y-1">
                <div>Created: {format(new Date(user.created_at), "dd MMM yyyy")}</div>
                {user.created_by && <div>By: {user.created_by}</div>}
              </div>
              {user.username === currentUsername && (
                <div className="mt-3 text-xs text-[#00D26A] flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Currently logged in
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="admin-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedUser ? "Edit Admin User" : "Add New Admin User"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-slate-300">Username</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="form-input mt-1"
                placeholder="Enter username"
                required
                disabled={!!selectedUser}
                data-testid="input-admin-username"
              />
              {selectedUser && (
                <p className="text-xs text-slate-500 mt-1">Username cannot be changed</p>
              )}
            </div>
            <div>
              <Label htmlFor="name" className="text-slate-300">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input mt-1"
                placeholder="Enter full name"
                required
                data-testid="input-admin-name"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">
                Password {selectedUser && "(leave blank to keep current)"}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="form-input mt-1 pr-10"
                  placeholder={selectedUser ? "Enter new password" : "Enter password"}
                  required={!selectedUser}
                  data-testid="input-admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsDialogOpen(false)}
                className="btn-ghost"
                data-testid="cancel-admin-btn"
              >
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-admin-btn">
                {selectedUser ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-800" data-testid="delete-admin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Admin User</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to delete &quot;{selectedUser?.name}&quot; (@{selectedUser?.username})? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-ghost" data-testid="cancel-delete-admin-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="btn-danger" data-testid="confirm-delete-admin-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
